import { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Box,
  Typography,
  FormControlLabel,
  Checkbox,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  IconButton,
  Divider,
  Paper,
  FormControl,
  Select,
  MenuItem,
  CircularProgress,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import axios from 'axios';

const BASE =
  process.env.REACT_APP_BACKEND_ROUTE_URL || 'http://localhost.dev.com:8000/app/api/';

const getHeaders = () => ({
  Authorization: `Bearer ${sessionStorage.getItem('accessToken') || ''}`,
  'APP-MODE': sessionStorage.getItem('APP_MODE') || '',
  'ORG-CODE': sessionStorage.getItem('ORG_CODE') || '',
  'USER-ACCESS-KEY': sessionStorage.getItem('USER_ACCESS_KEY') || '',
  USERNAME: sessionStorage.getItem('userName') || '',
});

const toLabel = (field = '') =>
  field.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());

const extractList = (raw) => {
  if (Array.isArray(raw?.data)) return raw.data;
  if (Array.isArray(raw?.results)) return raw.results;
  if (Array.isArray(raw)) return raw;
  return [];
};

const normalizeOption = (item) => {
  if (typeof item === 'string') return { label: item, value: item };
  const label = item.name || item.label || item.display_name || String(item.id || '');
  const value = item.id || item.value || item.name || label;
  return { label: String(label), value: String(value) };
};

const emptyRow = (columns = []) =>
  columns.reduce(
    (acc, col) => ({ ...acc, [col.field]: col.type === 'boolean' ? false : '' }),
    {}
  );

const buildInitialState = (structure = []) =>
  structure.reduce((acc, field) => {
    acc[field.field] = field.type === 'table' ? [] : '';
    return acc;
  }, {});

const ClinicalNotesModal = ({ open, onClose, onSubmit, structure = [] }) => {
  const [formData, setFormData] = useState({});
  const [addToTaskList, setAddToTaskList] = useState(false);
  // Cache for reference-based select options, keyed by reference name
  const [refOptions, setRefOptions] = useState({});
  const [refLoading, setRefLoading] = useState({});

  // Reset form on open
  useEffect(() => {
    if (open) {
      setFormData(buildInitialState(structure));
      setAddToTaskList(false);
    }
  }, [open, structure]);

  // Fetch options for all `select` columns that have a `reference` field
  useEffect(() => {
    if (!open) return;
    const refs = new Set();
    structure.forEach((field) => {
      if (field.type === 'table') {
        (field.columns || []).forEach((col) => {
          if (col.type === 'select' && col.reference) refs.add(col.reference);
        });
      }
    });

    refs.forEach((ref) => {
      if (refOptions[ref] !== undefined) return; // already fetched or in-flight
      setRefLoading((prev) => ({ ...prev, [ref]: true }));
      axios
        .get(`${BASE}${ref}/`, { headers: getHeaders() })
        .then((r) => {
          const list = extractList(r.data).map(normalizeOption);
          setRefOptions((prev) => ({ ...prev, [ref]: list }));
        })
        .catch(() => {
          setRefOptions((prev) => ({ ...prev, [ref]: [] }));
        })
        .finally(() => {
          setRefLoading((prev) => ({ ...prev, [ref]: false }));
        });
    });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, structure]);

  const handleTextChange = (fieldName, value) =>
    setFormData((prev) => ({ ...prev, [fieldName]: value }));

  const handleAddRow = (fieldName, columns) =>
    setFormData((prev) => ({
      ...prev,
      [fieldName]: [...(prev[fieldName] || []), emptyRow(columns)],
    }));

  const handleRemoveRow = (fieldName, rowIndex) =>
    setFormData((prev) => ({
      ...prev,
      [fieldName]: (prev[fieldName] || []).filter((_, i) => i !== rowIndex),
    }));

  const handleCellChange = (fieldName, rowIndex, colField, value) =>
    setFormData((prev) => {
      const rows = [...(prev[fieldName] || [])];
      rows[rowIndex] = { ...rows[rowIndex], [colField]: value };
      return { ...prev, [fieldName]: rows };
    });

  const hasAnyValue = () =>
    structure.some((field) => {
      const val = formData[field.field];
      if (field.type === 'table')
        return (
          Array.isArray(val) &&
          val.some((row) =>
            Object.values(row).some((v) => String(v || '').trim() !== '')
          )
        );
      return typeof val === 'string' && val.trim() !== '';
    });

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!hasAnyValue()) return;
    onSubmit(formData, { addToTaskList });
    onClose();
  };

  const renderTableCell = (field, col, row, rowIndex) => {
    // Select column (static options or reference-fetched)
    if (col.type === 'select') {
      const opts =
        col.reference
          ? (refOptions[col.reference] || [])
          : (col.options || []).map(normalizeOption);
      const loading = Boolean(col.reference && refLoading[col.reference]);
      return (
        <TableCell key={col.field} sx={{ py: 0.5, px: 1, minWidth: 140 }}>
          <FormControl fullWidth size="small">
            <Select
              value={row[col.field] || ''}
              onChange={(e) => handleCellChange(field.field, rowIndex, col.field, e.target.value)}
              displayEmpty
              endAdornment={loading ? <CircularProgress size={14} sx={{ mr: 2 }} /> : null}
              sx={{ fontSize: '0.78rem' }}
            >
              <MenuItem value="" disabled sx={{ fontSize: '0.78rem' }}>
                <em>Select…</em>
              </MenuItem>
              {opts.map((opt) => (
                <MenuItem key={opt.value} value={opt.value} sx={{ fontSize: '0.82rem' }}>
                  {opt.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </TableCell>
      );
    }

    // Date column
    if (col.type === 'date') {
      return (
        <TableCell key={col.field} sx={{ py: 0.5, px: 1, minWidth: 140 }}>
          <TextField
            size="small"
            fullWidth
            type="date"
            value={row[col.field] || ''}
            onChange={(e) => handleCellChange(field.field, rowIndex, col.field, e.target.value)}
            required={col.required}
            InputLabelProps={{ shrink: true }}
            sx={{ '& .MuiInputBase-input': { fontSize: '0.78rem', py: '5px' } }}
          />
        </TableCell>
      );
    }

    // Number / text column (default)
    return (
      <TableCell key={col.field} sx={{ py: 0.5, px: 1 }}>
        <TextField
          size="small"
          fullWidth
          type={col.type === 'number' ? 'number' : 'text'}
          value={row[col.field] || ''}
          onChange={(e) => handleCellChange(field.field, rowIndex, col.field, e.target.value)}
          required={col.required}
          sx={{ '& .MuiInputBase-input': { fontSize: '0.78rem', py: '5px' } }}
        />
      </TableCell>
    );
  };

  const renderField = (field) => {
    const label = field.label || toLabel(field.field);

    if (field.type === 'table') {
      const rows = formData[field.field] || [];
      const columns = field.columns || [];
      return (
        <Box key={field.field} sx={{ mb: 3 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
            <Typography sx={{ fontWeight: 600, fontSize: '0.88rem', color: '#121959' }}>
              {label}
              {!field.required && (
                <Typography component="span" sx={{ fontSize: '0.75rem', color: 'text.secondary', ml: 0.5 }}>
                  (Optional)
                </Typography>
              )}
            </Typography>
            <Button
              size="small"
              startIcon={<AddIcon />}
              onClick={() => handleAddRow(field.field, columns)}
              sx={{ fontSize: '0.73rem', textTransform: 'none', color: '#21cdc0' }}
            >
              Add Row
            </Button>
          </Box>

          {rows.length > 0 ? (
            <Paper variant="outlined" sx={{ borderRadius: 1.5, overflow: 'hidden' }}>
              <Table size="small">
                <TableHead>
                  <TableRow sx={{ backgroundColor: '#f5fffe' }}>
                    {columns.map((col) => (
                      <TableCell
                        key={col.field}
                        sx={{ fontWeight: 700, fontSize: '0.73rem', color: '#374151', py: 0.8, whiteSpace: 'nowrap' }}
                      >
                        {col.label || toLabel(col.field)}
                        {col.required && (
                          <Typography component="span" sx={{ color: 'error.main', ml: 0.3 }}>*</Typography>
                        )}
                      </TableCell>
                    ))}
                    <TableCell sx={{ width: 36 }} />
                  </TableRow>
                </TableHead>
                <TableBody>
                  {rows.map((row, rowIndex) => (
                    <TableRow key={rowIndex}>
                      {columns.map((col) => renderTableCell(field, col, row, rowIndex))}
                      <TableCell sx={{ py: 0.5, px: 0.5 }}>
                        <IconButton
                          size="small"
                          onClick={() => handleRemoveRow(field.field, rowIndex)}
                          sx={{ color: '#ef4444', '&:hover': { backgroundColor: '#fef2f2' } }}
                        >
                          <DeleteOutlineIcon sx={{ fontSize: '1rem' }} />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </Paper>
          ) : (
            <Box
              onClick={() => handleAddRow(field.field, columns)}
              sx={{
                border: '1px dashed #d1d5db', borderRadius: 1.5, p: 1.5,
                textAlign: 'center', cursor: 'pointer',
                '&:hover': { borderColor: '#21cdc0', backgroundColor: '#f5fffe' },
              }}
            >
              <Typography variant="caption" color="text.secondary">
                Click "+ Add Row" to add entries
              </Typography>
            </Box>
          )}
        </Box>
      );
    }

    // Text field — multiline for clinical notes (they're always long-form)
    return (
      <Box key={field.field} sx={{ mb: 2 }}>
        <Typography sx={{ fontWeight: 500, fontSize: '0.85rem', color: '#374151', mb: 0.5 }}>
          {label}
          {field.required
            ? <Typography component="span" sx={{ color: 'error.main', ml: 0.3 }}>*</Typography>
            : <Typography component="span" sx={{ fontSize: '0.75rem', color: 'text.secondary', ml: 0.5 }}>(Optional)</Typography>
          }
        </Typography>
        <TextField
          fullWidth
          size="small"
          multiline
          rows={3}
          value={formData[field.field] || ''}
          onChange={(e) => handleTextChange(field.field, e.target.value)}
          required={field.required}
          placeholder={`Enter ${label.toLowerCase()}`}
        />
      </Box>
    );
  };

  const renderFallback = () =>
    ['condition', 'goals', 'notes'].map((f) => (
      <Box key={f} sx={{ mb: 2 }}>
        <Typography sx={{ fontWeight: 500, fontSize: '0.85rem', color: '#374151', mb: 0.5 }}>
          {toLabel(f)}
          <Typography component="span" sx={{ fontSize: '0.75rem', color: 'text.secondary', ml: 0.5 }}>(Optional)</Typography>
        </Typography>
        <TextField
          fullWidth size="small"
          multiline={f === 'notes'} rows={f === 'notes' ? 3 : 1}
          value={formData[f] || ''}
          onChange={(e) => handleTextChange(f, e.target.value)}
        />
      </Box>
    ));

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle sx={{ fontWeight: 700, fontSize: '1rem', color: '#121959', pb: 1 }}>
        Clinical Notes
      </DialogTitle>
      <Divider />
      <form onSubmit={handleSubmit}>
        <DialogContent sx={{ pt: 2, maxHeight: '70vh' }}>
          {structure.length > 0 ? structure.map(renderField) : renderFallback()}
        </DialogContent>
        <Divider />
        <DialogActions sx={{ px: 3, py: 1.5, display: 'flex', justifyContent: 'space-between' }}>
          <FormControlLabel
            control={
              <Checkbox
                size="small"
                checked={addToTaskList}
                onChange={(e) => setAddToTaskList(e.target.checked)}
              />
            }
            label={<Typography sx={{ fontSize: '0.82rem' }}>Add to task list</Typography>}
          />
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Button onClick={onClose} variant="outlined" size="small">CANCEL</Button>
            <Button
              type="submit"
              variant="contained"
              size="small"
              disabled={!hasAnyValue()}
              sx={{ backgroundColor: '#0E3F66', '&:hover': { backgroundColor: '#0a2e4d' } }}
            >
              SAVE ACTIVITY
            </Button>
          </Box>
        </DialogActions>
      </form>
    </Dialog>
  );
};

export default ClinicalNotesModal;
